import './Modal.scss';

import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'core/redux/reducers';
import cls from 'classnames';
import { actions } from 'core/redux/features/shared';
import { NotificationButton } from 'core/redux/features/shared/types';
import { selectTranslator } from 'core/redux/features/languages/trans';
import { createPortal } from 'react-dom';

export function Modal() {
  const trans = useSelector(selectTranslator);
  const { id, body, buttons, title, message, onCloseCallback, open } =
    useSelector((state: AppState) => state.notification);
  const dispatch = useDispatch();

  /**
   * Handle close button
   */
  const handleCloseButton = function () {
    const closeModal = () => dispatch(actions.notifyClose());
    if (onCloseCallback) {
      onCloseCallback()
        .then(() => {
          closeModal();
        })
        .catch(console.warn);
    } else {
      closeModal();
    }
  };

  /**
   * Handle press button
   * @params {NotificationButton} btn
   */
  const handlePressButton = async function ({
    clickCallback,
  }: NotificationButton) {
    if (clickCallback) {
      try {
        await clickCallback();
      } catch (e) {
        console.warn(e);
      }
    }

    // close modal
    dispatch(actions.notifyClose());
  };

  const modalRoot: HTMLElement | null =
    typeof document !== 'undefined' ? document.body : null;
  const mountToEl = useRef<Element>(document.createElement('div'));

  useEffect(() => {
    if (!modalRoot) {
      return;
    }
    const el = mountToEl.current;
    modalRoot.appendChild(el);
    return () => {
      modalRoot.removeChild(el);
    };
  }, [modalRoot]);

  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div
      data-testid="notif"
      className={cls('modal', open && 'is-active')}
      id={id}
    >
      <div className="modal-background" />
      <div className="notification">
        <button
          data-testid="notif.close"
          onClick={handleCloseButton}
          className="delete"
          data-target="#error-modal"
        />
        {!body || body === '' ? (
          <>
            <h5
              style={{ whiteSpace: 'break-spaces' }}
              className="title is-6"
              dangerouslySetInnerHTML={{ __html: trans(title, {}, '') || '' }}
            />
            <p className="subtitle is-7">{message || ''}</p>
            {buttons.length > 0 ? (
              <div className="buttons">
                {buttons.map(btn => {
                  return (
                    <button
                      key={btn.label}
                      onClick={_ => handlePressButton(btn)}
                      className={`button ${btn.className}`}
                    >
                      {trans(btn.label, {}, '')}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: body }} />
        )}
      </div>
    </div>,
    mountToEl.current,
  );
}

export default Modal;
